// filepath: apps/web/src/features/admin/effectsMetrics/CircuitOpenBanner.tsx
//
// FR-UI-ADM-9 AC-4 — dismissible circuit-open banner.
//
// role="alert" + aria-live="assertive" (WCAG 4.1.3).
//
// Dismissal persists in sessionStorage keyed by
// `ascoa.dismissedCircuitBanners` with payload shape
// `{ "<tenantSlug>:<endpoint_key>": <unix_ms> }`. No token, no
// secret, no event id (INV-5 — verified by static guard).
//
// Reappears after 60-minute cooldown when the same endpoint opens
// again (AC-4 / I-7).

import React from "react";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "ascoa.dismissedCircuitBanners";
const COOLDOWN_MS = 60 * 60 * 1000;

type DismissalMap = Record<string, number>;

function readDismissals(): DismissalMap {
  if (typeof sessionStorage === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as DismissalMap;
  } catch {
    return {};
  }
}

function writeDismissal(
  tenantSlug: string,
  endpointKey: string,
  unixMs: number,
): void {
  if (typeof sessionStorage === "undefined") return;
  const map = readDismissals();
  map[`${tenantSlug}:${endpointKey}`] = unixMs;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

function isDismissed(
  tenantSlug: string,
  endpointKey: string,
  openedAtUnixMs: number,
): boolean {
  const map = readDismissals();
  const key = `${tenantSlug}:${endpointKey}`;
  const dismissedAt = map[key];
  if (typeof dismissedAt !== "number") return false;
  // Reappear if either: (a) the circuit re-opened after the dismissal, OR
  // (b) the 60-min cooldown has elapsed.
  if (openedAtUnixMs > dismissedAt) return false;
  return Date.now() - dismissedAt < COOLDOWN_MS;
}

interface CircuitOpenBannerProps {
  tenantSlug: string;
  /** Endpoint keys that have just transitioned CLOSED → OPEN between polls. */
  openedEndpointKeys: string[];
  /** Map of endpoint key → opened_at_unix_ms for the current snapshot. */
  openedAtByKey: Record<string, number>;
}

export function CircuitOpenBanner({
  tenantSlug,
  openedEndpointKeys,
  openedAtByKey,
}: CircuitOpenBannerProps): React.ReactElement | null {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = React.useState<Set<string>>(
    () => new Set(),
  );

  const visible = openedEndpointKeys.filter((ek) => {
    if (dismissed.has(ek)) return false;
    const openedAt = openedAtByKey[ek] ?? 0;
    return !isDismissed(tenantSlug, ek, openedAt);
  });

  if (visible.length === 0) return null;

  const handleDismiss = (ek: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(ek);
      return next;
    });
    writeDismissal(tenantSlug, ek, Date.now());
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="circuit-open-banner"
      data-testid="circuit-open-banner"
    >
      <ul>
        {visible.map((ek) => (
          <li key={ek}>
            <span>
              {t(
                "admin.effects.metrics.circuit_open_banner",
                "Circuit open for {{endpoint}}. ",
                { endpoint: ek },
              )}
              {t(
                "admin.effects.metrics.circuit_open_affected",
                "Affected deliveries queued for retry.",
              )}
            </span>
            <button
              type="button"
              onClick={() => handleDismiss(ek)}
              aria-label={t(
                "admin.effects.metrics.banner_dismiss_label",
                "Dismiss circuit-open notice for {{endpoint}}",
                { endpoint: ek },
              )}
            >
              {t("admin.effects.metrics.banner_dismiss", "Dismiss")}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
